'use client';

import Link from 'next/link';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';

import { useT } from '@/hooks';
import { LeaderboardTrader } from '@/types';
import { formatLeaderboardCurrency, getAvatarGradient } from '@/utils';

import { ROUTES } from '@/constants';

interface LeaderboardMobileCardProps {
  entry: LeaderboardTrader;
  index: number;
  orderBy: 'PNL' | 'VOL';
}

// Splits a username on camelCase / digit / punctuation boundaries so initials
// read like "ferrariChampion" -> "FC" instead of just its first two letters.
const getTraderInitials = (name?: string): string => {
  if (!name) return '?';
  const spaced = name
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ')
    .replace(/([a-zA-Z])(\d)/g, '$1 $2')
    .trim();
  const parts = spaced.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

export const LeaderboardMobileCard = ({ entry, index, orderBy }: LeaderboardMobileCardProps) => {
  const theme = useTheme();
  const { t } = useT();

  const rank = entry?.winRank || index + 1;

  const profitLoss = `${entry?.pnl > 0 ? '+' : ''}${formatLeaderboardCurrency(entry?.pnl || 0)}`;
  const profitLossValue = profitLoss === '$0' ? '-' : profitLoss;
  const volume = formatLeaderboardCurrency(entry?.volume || 0);
  const volumeValue = volume === '$0' ? '-' : volume;
  const pnlColor =
    entry?.pnl > 0 ? '#4ec38a' : entry?.pnl < 0 ? '#e0664f' : theme.palette.text.primary;

  const displayValue = orderBy === 'VOL' ? volumeValue : profitLossValue;
  const displayColor = orderBy === 'VOL' ? theme.palette.text.primary : pnlColor;

  const userName = entry?.userName || 'Unknown Trader';
  const tradesLabel = `${(entry?.tradesCount ?? 0).toLocaleString()} ${t('leaderboard.trades').toUpperCase()}`;
  const gradient = entry?.profileImage || getAvatarGradient(entry?.address || '', entry?.id, index);

  const isTopRank = rank === 1;

  return (
    <Box
      component={Link}
      href={ROUTES.LEADERBOARD_PORTFOLIO(entry?.address || entry?.id || index.toString())}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.75,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        textDecoration: 'none',
        transition: 'background-color 0.15s ease',
        '&:hover': { bgcolor: isTopRank ? 'rgba(91, 140, 255, 0.16)' : 'action.hover' },
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      {/* Rank */}
      <Typography
        sx={{
          width: 20,
          flexShrink: 0,
          textAlign: 'center',
          fontSize: '0.875rem',
          fontWeight: 700,
          color: isTopRank ? '#5b8cff' : 'text.secondary',
        }}
      >
        {rank}
      </Typography>

      {/* Avatar */}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          border: '2px solid',
          borderColor: 'divider',
          color: 'white',
          fontWeight: 700,
          fontSize: '0.8125rem',
          letterSpacing: '0.02em',
        }}
      >
        {getTraderInitials(userName)}
      </Box>

      {/* Name + trades count */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          noWrap
          sx={{ fontSize: '0.9375rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}
        >
          {userName}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.04em',
            color: 'text.secondary',
            mt: '2px',
          }}
        >
          {tradesLabel}
        </Typography>
      </Box>

      {/* PNL / Volume */}
      <Typography
        sx={{ fontSize: '0.9375rem', fontWeight: 700, color: displayColor, flexShrink: 0 }}
      >
        {displayValue}
      </Typography>
    </Box>
  );
};

export const LeaderboardMobileCardSkeleton = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.75,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Skeleton variant="text" width={16} height={20} />
      <Skeleton variant="circular" width={40} height={40} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width="55%" height={20} />
        <Skeleton variant="text" width="35%" height={14} sx={{ mt: '2px' }} />
      </Box>
      <Skeleton variant="text" width={64} height={20} />
    </Box>
  );
};
